import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import HelpTip from "@/components/HelpTip";
import { toast } from "sonner";

// Convert hex -> HSL string "H S% L%"
const hexToHsl = (hex) => {
    let r = parseInt(hex.slice(1, 3), 16) / 255;
    let g = parseInt(hex.slice(3, 5), 16) / 255;
    let b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
            default: break;
        }
        h /= 6;
    }
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

const PRESETS = [
    { name: "Signal Red (default)", color: "#FF3B30" },
    { name: "Electric Lime", color: "#A8FF00" },
    { name: "Cyber Yellow", color: "#FFD400" },
    { name: "Hot Pink", color: "#FF2E88" },
    { name: "Deep Violet", color: "#7B2CFF" },
    { name: "Ocean", color: "#00A3FF" },
    { name: "Olive", color: "#A3B86C" },
];

const GOOGLE_FONTS = ["Manrope", "Inter", "Space Grotesk", "DM Sans", "Poppins", "Montserrat", "Clash Display", "Playfair Display", "Bebas Neue", "Oswald", "Archivo Black", "Anton", "JetBrains Mono", "IBM Plex Mono"];

const DEFAULT_THEME = {
    accent_color: "#FF3B30", accent_hsl: "4 90% 58%",
    bg_color: "#FAFAFA", text_color: "#0A0A0A",
    button_bg: "#0A0A0A", button_text: "#FFFFFF",
    heading_font: "Clash Display", body_font: "Manrope",
    button_radius: 0, dark_mode: false,
};

export default function AdminTheme() {
    const [t, setT] = useState(null);
    const [history, setHistory] = useState([]);
    const [saving, setSaving] = useState(false);
    const { setThemeLocal } = useSite();

    const load = async () => {
        const [cur, hist] = await Promise.all([api.get("/theme"), api.get("/admin/theme/history")]);
        setT(cur.data); setHistory(hist.data);
    };
    useEffect(() => { load(); }, []);

    const update = (patch) => {
        const next = { ...t, ...patch };
        if (patch.accent_color) next.accent_hsl = hexToHsl(patch.accent_color);
        setT(next);
        setThemeLocal(next); // live preview
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/admin/theme", t);
            setT(data); setThemeLocal(data);
            toast.success("Theme saved");
            load();
        } catch { toast.error("Save failed"); }
        finally { setSaving(false); }
    };

    const restore = async (sid) => {
        if (!window.confirm("Restore this theme?")) return;
        const { data } = await api.post(`/admin/theme/restore/${sid}`);
        setT(data); setThemeLocal(data); toast.success("Restored"); load();
    };

    if (!t) return <div className="font-mono text-sm">Loading…</div>;

    return (
        <div data-testid="admin-theme">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                    <h1 className="font-display text-4xl tracking-tighter">Theme</h1>
                    <HelpTip>Change the full brand look. Preview updates live; click Save to apply, or Reset to restore the Clonzo default.</HelpTip>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => { update(DEFAULT_THEME); toast.success("Reset to default (click Save to apply)"); }} className="border-2 border-black px-4 py-3 font-mono text-xs uppercase">Reset to Default</button>
                    <button onClick={save} disabled={saving} className="bg-black text-white px-5 py-3 font-mono text-xs uppercase brutal-shadow" data-testid="theme-save">
                        {saving ? "Saving…" : "Save Theme"}
                    </button>
                </div>
            </div>

            <div className="bg-white border-2 border-black p-6 space-y-6">
                <div>
                    <div className="font-mono text-[10px] uppercase text-neutral-500 mb-2 flex items-center gap-2">
                        Accent Color
                        <HelpTip>Used on highlighted text (hero accent), buttons, badges, sale banners.</HelpTip>
                    </div>
                    <div className="flex items-center gap-4">
                        <input type="color" value={t.accent_color} onChange={e => update({ accent_color: e.target.value })} className="w-16 h-16 border-2 border-black p-1" data-testid="theme-color-picker" />
                        <input type="text" value={t.accent_color} onChange={e => update({ accent_color: e.target.value })} className="border-2 border-black p-3 font-mono text-sm w-40 uppercase" />
                        <div className="font-mono text-xs text-neutral-500">HSL: {t.accent_hsl}</div>
                    </div>
                </div>

                <div>
                    <div className="font-mono text-[10px] uppercase text-neutral-500 mb-2">Quick Presets</div>
                    <div className="flex flex-wrap gap-2">
                        {PRESETS.map(p => (
                            <button key={p.name} type="button" onClick={() => update({ accent_color: p.color })}
                                className="flex items-center gap-2 border-2 border-black px-3 py-2 bg-white hover:bg-neutral-100">
                                <span className="w-5 h-5 border border-black" style={{ background: p.color }} />
                                <span className="font-mono text-xs">{p.name}</span>
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <div className="font-mono text-[10px] uppercase text-neutral-500 mb-2">Preview</div>
                    <div className="border-2 border-black p-6 space-y-3" style={{ background: t.bg_color, color: t.text_color }}>
                        <div className="flex items-center gap-3">
                            <button className="px-4 py-2 border-2 font-mono text-xs uppercase" style={{ background: t.button_bg, color: t.button_text, borderColor: t.button_bg, borderRadius: t.button_radius + "px" }}>Primary Action</button>
                            <span className="text-2xl" style={{ fontFamily: `"${t.heading_font}", sans-serif` }}>Heading <span style={{ color: t.accent_color }}>Accent</span></span>
                        </div>
                        <div style={{ fontFamily: `"${t.body_font}", sans-serif` }}>The quick brown fox jumps over the lazy dog.</div>
                        <div className="px-4 py-2" style={{ background: t.accent_color, color: "#fff" }}>▶ Monsoon Sale — Up to 30% Off ◀</div>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <div className="font-mono text-[10px] uppercase text-neutral-500 mb-2">Background Color</div>
                        <div className="flex items-center gap-3">
                            <input type="color" value={t.bg_color} onChange={e => update({ bg_color: e.target.value })} className="w-16 h-12 border-2 border-black" />
                            <input value={t.bg_color} onChange={e => update({ bg_color: e.target.value })} className="border-2 border-black p-3 font-mono text-sm w-32 uppercase" />
                        </div>
                    </div>
                    <div>
                        <div className="font-mono text-[10px] uppercase text-neutral-500 mb-2">Text Color</div>
                        <div className="flex items-center gap-3">
                            <input type="color" value={t.text_color} onChange={e => update({ text_color: e.target.value })} className="w-16 h-12 border-2 border-black" />
                            <input value={t.text_color} onChange={e => update({ text_color: e.target.value })} className="border-2 border-black p-3 font-mono text-sm w-32 uppercase" />
                        </div>
                    </div>
                    <div>
                        <div className="font-mono text-[10px] uppercase text-neutral-500 mb-2">Button Background</div>
                        <div className="flex items-center gap-3">
                            <input type="color" value={t.button_bg} onChange={e => update({ button_bg: e.target.value })} className="w-16 h-12 border-2 border-black" />
                            <input value={t.button_bg} onChange={e => update({ button_bg: e.target.value })} className="border-2 border-black p-3 font-mono text-sm w-32 uppercase" />
                        </div>
                    </div>
                    <div>
                        <div className="font-mono text-[10px] uppercase text-neutral-500 mb-2">Button Text Color</div>
                        <div className="flex items-center gap-3">
                            <input type="color" value={t.button_text} onChange={e => update({ button_text: e.target.value })} className="w-16 h-12 border-2 border-black" />
                            <input value={t.button_text} onChange={e => update({ button_text: e.target.value })} className="border-2 border-black p-3 font-mono text-sm w-32 uppercase" />
                        </div>
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                    <div>
                        <div className="font-mono text-[10px] uppercase text-neutral-500 mb-2">Heading Font</div>
                        <select value={t.heading_font} onChange={e => update({ heading_font: e.target.value })} className="w-full border-2 border-black p-3 font-mono text-sm">
                            {GOOGLE_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                    </div>
                    <div>
                        <div className="font-mono text-[10px] uppercase text-neutral-500 mb-2">Body Font</div>
                        <select value={t.body_font} onChange={e => update({ body_font: e.target.value })} className="w-full border-2 border-black p-3 font-mono text-sm">
                            {GOOGLE_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                    </div>
                    <div>
                        <div className="font-mono text-[10px] uppercase text-neutral-500 mb-2">Button Style</div>
                        <select value={t.button_radius} onChange={e => update({ button_radius: Number(e.target.value) })} className="w-full border-2 border-black p-3 font-mono text-sm">
                            <option value={0}>Sharp (0px)</option>
                            <option value={4}>Soft (4px)</option>
                            <option value={8}>Rounded (8px)</option>
                            <option value={999}>Pill (999px)</option>
                        </select>
                    </div>
                </div>

                <label className="flex items-center gap-2 font-mono text-xs uppercase">
                    <input type="checkbox" checked={!!t.dark_mode} onChange={e => update({ dark_mode: e.target.checked })} />
                    Dark Mode (inverts background + text)
                </label>
            </div>

            {history.length > 0 && (
                <div className="bg-white border-2 border-black p-6 mt-4">
                    <div className="font-mono text-xs uppercase mb-3">Version History</div>
                    <div className="space-y-2">
                        {history.map(h => (
                            <div key={h.snapshot_id} className="flex items-center justify-between border-2 border-black p-3">
                                <div className="flex items-center gap-3">
                                    <span className="w-6 h-6 border border-black" style={{ background: h.accent_color }} />
                                    <div className="font-mono text-xs">{h.accent_color} — {new Date(h.snapshot_at).toLocaleString()}</div>
                                </div>
                                <button onClick={() => restore(h.snapshot_id)} className="font-mono text-xs uppercase underline">Restore</button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
